import React, { useState, useEffect, useContext } from "react";
import {
  Box, Typography, IconButton, Tooltip, Chip,
  CircularProgress, useTheme, Collapse
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import EditorContext from "../../../../contexts/EditorContext";
import { useTranslation } from "react-i18next";
import { formatHtmlForDisplay } from "../../../../utils/utils";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';

const Explanation = ({ contractId, startClauseExtraction }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t, i18n } = useTranslation();
  const clauses = useSelector((state) => state.contract.clauses || []);
  const loadingClause = useSelector((state) => state.contract.loadingClause);
  const hasClauses = clauses && clauses.length > 0;
  const [expandedIndex, setExpandedIndex] = useState(null);
  const isRTL = i18n.language === 'ar';
  const editorRef = useContext(EditorContext);

  const clearHighlights = () => {
    const editor = editorRef?.current?.getEditor();
    if (editor?.commands?.clearRiskHighlight) editor.commands.clearRiskHighlight();
  };

  const scrollToHighlight = () => {
    setTimeout(() => {
      const els = document.querySelectorAll('.risk-highlight');
      if (els.length > 0) {
        els[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        ReactGA.event({ category: 'Clause Interaction', action: 'Scrolled to Highlighted Clause' });
        tracker.event({ category: 'Clause Interaction', action: 'Scrolled to Highlighted Clause' });
      }
    }, 300);
  };

  const handleExtractClauses = async () => {
    if (!contractId) return;
    clearHighlights();
    ReactGA.event({ category: "Clause Extraction", action: "Clicked Extract Button", label: hasClauses ? "Re-extract" : "Initial Extract" });
    tracker.event({ category: "Clause Extraction", action: "Clicked Extract Button", label: hasClauses ? "Re-extract" : "Initial Extract" });
    await startClauseExtraction();
  };

  const handleToggle = (clause, index) => {
    const isExpanding = expandedIndex !== index;
    setExpandedIndex(isExpanding ? index : null);

    if (isExpanding) {
      ReactGA.event({ category: 'Clause Interaction', action: 'Expanded Clause', label: clause.title });
      tracker.event({ category: 'Clause Interaction', action: 'Expanded Clause', label: clause.title });
    }

    const editor = editorRef?.current?.getEditor();
    if (editor) {
      clearHighlights();
      if (isExpanding && clause.text) {
        try {
          editor.commands.highlightRiskText(clause.text, theme.palette.primary.main);
          scrollToHighlight();
        } catch (err) {
          console.error("Error applying highlight:", err);
        }
      }
    }
  };

  useEffect(() => () => clearHighlights(), []);

  return (
    <Box sx={{ width: "100%", height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        <Chip
          label={t("riskAnalysis.Chip")}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
            color: theme.palette.primary.main,
            fontWeight: 600, fontSize: '0.68rem', height: 20, mb: 0.75,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {t("clause_extract")}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, mt: 0.25 }}>
              {t("clause_extract_subtitle")}
            </Typography>
          </Box>
          <Tooltip title={hasClauses ? t("reextract_clauses") : t("extract_clauses")}>
            <span>
              <IconButton
                onClick={handleExtractClauses}
                disabled={loadingClause}
                size="small"
                sx={{
                  mt: 0.25, width: 30, height: 30,
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  borderRadius: '8px', color: theme.palette.text.secondary,
                  transition: 'all 0.15s',
                  '&:hover': { color: theme.palette.text.primary, borderColor: alpha(theme.palette.text.secondary, 0.5) },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                {loadingClause ? <CircularProgress size={13} color="inherit" /> : hasClauses ? <RefreshIcon sx={{ fontSize: 15 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Clauses list ── */}
      {hasClauses ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {clauses.map((clause, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <Box
                key={index}
                sx={{
                  borderRadius: '10px',
                  border: `1px solid ${isExpanded ? alpha(theme.palette.primary.main, 0.35) : alpha(theme.palette.divider, 0.6)}`,
                  backgroundColor: isDark ? alpha('#fff', 0.02) : '#fff',
                  overflow: 'hidden',
                  transition: 'border-color 0.16s',
                }}
              >
                {/* Summary row */}
                <Box
                  onClick={() => handleToggle(clause, index)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: 1.5, py: 1.1,
                    cursor: 'pointer', userSelect: 'none',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '0.82rem', textAlign: 'left', pr: 1 }}>
                    {clause.title}
                  </Typography>
                  <Tooltip title={clause.description || clause.title} arrow placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ color: alpha(theme.palette.text.secondary, 0.6), width: 22, height: 22, mr: 0.5 }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{
                    color: isExpanded ? theme.palette.primary.main : theme.palette.text.disabled,
                    display: 'flex', transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}>
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>

                {/* Expanded content */}
                <Collapse in={isExpanded}>
                  <Box sx={{
                    px: 1.5, py: 1.25,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    backgroundColor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
                  }}>
                    <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.65, color: theme.palette.text.secondary, textAlign: 'left' }}>
                      {formatHtmlForDisplay(clause.text)}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ) : !loadingClause && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '12px',
            backgroundColor: alpha(theme.palette.text.secondary, 0.08),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 24, color: theme.palette.text.disabled }} />
          </Box>
          <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary, textAlign: 'center' }}>
            {t("click_button_extract_clauses")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Explanation;
